import { Nullable, NullableResultPromise } from "../base-types";
import { CultureCache } from "../caches/culture-cache";
import { DEFINITIONS } from "../constants";
import { CultureLoadOption } from "../contracts/querying/culture-load-option";
import { EntityLoadConfiguration, IEntityLoadConfiguration } from "../contracts/querying/entity-load-configuration";
import {
    ComparisonOperator,
    CompositeFilterOperator,
    CompositeQueryFilter,
    DefinitionQueryFilter,
    FilterDataType,
    PropertyQueryFilter,
} from "../contracts/querying/filters";
import { MemberLoadOption } from "../contracts/querying/member-load-option";
import { PropertyLoadOption } from "../contracts/querying/property-load-option";
import { Query } from "../contracts/querying/query";
import { RelationLoadOption } from "../contracts/querying/relation-load-option";
import CultureInfo from "../culture-info";
import ErrorMessages from "../error-messages";
import { InternalError } from "../errors/internal-error";
import Guard from "../guard";
import { IExtendedContentHubClient } from "./extended-client";

/**
 * Allows fetching culture information.
 */
export interface ICultureLoader {
    /**
     * Gets the default culture.
     * @returns The default culture.
     */
    getDefaultCultureAsync(): NullableResultPromise<CultureInfo>;

    /**
     * Gets the default culture (cached).
     * @returns The default cached culture.
     */
    getDefaultCultureCachedAsync(): NullableResultPromise<CultureInfo>;

    /**
     * Gets all installed cultures (cached).
     * @returns All cached cultures.
     */
    getAllCulturesCachedAsync(): Promise<Array<CultureInfo>>;

    /**
     * Gets all installed cultures.
     * @returns All cultures.
     */
    getAllCulturesAsync(): Promise<Array<CultureInfo>>;
}

export class CultureLoader implements ICultureLoader {
    private readonly _client: IExtendedContentHubClient;
    private readonly _cultureCache: CultureCache;

    private static readonly _loadConfig: IEntityLoadConfiguration = new EntityLoadConfiguration(
        CultureLoadOption.None,
        new PropertyLoadOption(DEFINITIONS["Setting"].value),
        RelationLoadOption.None
    );

    private static readonly _query: Query = new Query({
        filter: new CompositeQueryFilter({
            combineMethod: CompositeFilterOperator.And,
            children: [
                new DefinitionQueryFilter({
                    name: DEFINITIONS["Setting"].definitionName,
                    operator: ComparisonOperator.Equals,
                }),
                new PropertyQueryFilter({
                    operator: ComparisonOperator.Equals,
                    dataType: FilterDataType.String,
                    property: DEFINITIONS["Setting"].name,
                    value: DEFINITIONS["Setting"].CultureSetting.name,
                }),
            ],
        }),
        take: 1,
    });

    constructor(client: IExtendedContentHubClient) {
        Guard.notNull(client);

        this._client = client;
        this._cultureCache = new CultureCache();
    }

    async getDefaultCultureAsync(): NullableResultPromise<CultureInfo> {
        const cultures = await this.getAllCulturesAsync();

        return CultureLoader.extractDefaultCulture(cultures);
    }

    async getDefaultCultureCachedAsync(): NullableResultPromise<CultureInfo> {
        const cultures = await this.getAllCulturesCachedAsync();

        return CultureLoader.extractDefaultCulture(cultures);
    }

    async getAllCulturesAsync(): Promise<Array<CultureInfo>> {
        const cultures = await this.queryCultureAsync();
        this._cultureCache.loadCache(cultures);

        return cultures;
    }

    async getAllCulturesCachedAsync(): Promise<Array<CultureInfo>> {
        let cultures = this._cultureCache.getCachedCultures();

        if (cultures == null) {
            cultures = await this.getAllCulturesAsync();
        }

        return cultures;
    }

    //#region Private methods

    private async queryCultureAsync(): Promise<Array<CultureInfo>> {
        const result = await this._client.querying.singleAsync(CultureLoader._query, CultureLoader._loadConfig);

        if (result == null) {
            throw new InternalError(ErrorMessages.CultureLoader.CouldNotFindCultures);
        }
        const token = await result.getPropertyValueAsync(DEFINITIONS["Setting"].value, MemberLoadOption.LazyLoading);

        if (token == null) {
            throw new InternalError(ErrorMessages.CultureLoader.CouldNotFindCultures);
        }

        const cultures: Array<CultureInfo> =
            token instanceof Array
                ? (token as Array<CultureInfo>)
                : typeof token === "string"
                  ? (JSON.parse(token) as Array<CultureInfo>)
                  : [];
        return cultures;
    }

    private static extractDefaultCulture(cultures: Array<CultureInfo>): Nullable<CultureInfo> {
        if (cultures == null || cultures.length === 0) {
            return null;
        }

        return cultures[0];
    }

    //#endregion
}
